import React, { FunctionComponent } from 'react';
import { useLocale } from '../../contexts/LocaleProvider';
import { IntlProvider } from 'react-intl';
import { Box } from '@mui/material';
import ReusableTabs from '../common/ReusableTabs';
import * as styles from '../productPage/tableComponents/ProductPage_style';

import { PrefilteredResourceLibraryItemsProvider } from '../../graphql/pimcore/modules/resourceLibrary/contexts/PrefilteredResourceLibraryItemsProvider';
import AerospaceTemplate from './AerospaceTemplate';
import BreadCrumbs from '../common/BreadCrumbs';
import { linkButtons } from './LinkButtons';
import ToolPage from '../resourcesPage/simulationPage/ToolPage';
import TechnicalResources from '../automotivePage/TechnicalResources';
import { useApplicationsContent } from '../../graphql/pimcore/modules/siteContent/hooks/useApplicationsContent';
import DebouncedLoader from '../common/DebouncedLoader';
import {
    LinkCollectionElement,
    LinkCollectionEntry,
} from '../../graphql/pimcore/modules/siteContent/types/SiteContentElement';
import { useSalesAndResourcesSimulationToolsContent } from '../../graphql/pimcore/modules/siteContent/hooks/useSalesAndResourcesSimulationToolsContent';

interface AerospacePageProps {}

export interface Aerospace {
    pageName: string;
    title: string;
    description: string;
    image: string;
    linkTitle?: string;
    withButton?: boolean;
    buttonLink?: string;
    buttonText?: string;
}

const getLinkText = (link?: LinkCollectionEntry): string => {
    if (!link) return 'Products';
    return link.linkText;
};

const getLinkHref = (link?: LinkCollectionEntry): string => {
    if (!link) return '#';
    return link.kind === 'externalLinkFieldCollection' ? link.linkUrl : link.linkRoute;
};

const getLinkButtons = (link?: LinkCollectionElement): linkButtons[] => {
    if (!link) return [];
    return link.links.map(function (link) {
        return { title: link.linkText, searchTerms: getLinkHref(link) };
    });
};

const AerospacePage: FunctionComponent<AerospacePageProps> = () => {
    const { locale, messages } = useLocale();
    const { loading, error, contentTree } = useApplicationsContent('AerospaceDefenseAndMedical');
    const { contentTree: toolsContent } = useSalesAndResourcesSimulationToolsContent();
    if (error && !loading) {
        return <div className="mid-width">Error: {error.message}</div>;
    }

    const telecommunicationTemplates: Aerospace[] = [
        {
            pageName: contentTree.Aerospace.title.content,
            title: contentTree.Aerospace.title.content,
            description: contentTree.Aerospace.mainText.content,
            image: contentTree.Aerospace.mainImage.imageUrl,
            linkTitle: contentTree.Aerospace.linksTitle.content,
        },
        {
            pageName: contentTree.Defense.title.content,
            title: contentTree.Defense.title.content,
            description: contentTree.Defense.mainText.content,
            image: contentTree.Defense.mainImage.imageUrl,
            linkTitle: contentTree.Defense.linksTitle.content,
        },
        {
            pageName: contentTree.Avionics.title.content,
            title: contentTree.Avionics.title.content,
            description: contentTree.Avionics.mainText.content,
            image: contentTree.Avionics.mainImage.imageUrl,
            linkTitle: contentTree.Avionics.linksTitle.content,
        },
        {
            pageName: contentTree.Medical.title.content,
            title: contentTree.Medical.title.content,
            description: contentTree.Medical.mainText.content,
            image: contentTree.Medical.mainImage.imageUrl,
            withButton: true,
            buttonLink: getLinkHref(contentTree.Medical.links.links[0]),
            buttonText: getLinkText(contentTree.Medical.links.links[0]),
        },
    ];

    const aerospaceLinkButtons: linkButtons[] = getLinkButtons(contentTree.Aerospace.links);

    const avionicsLinkButtons: linkButtons[] = getLinkButtons(contentTree.Avionics.links);

    const defenseLinkButtons: linkButtons[] = getLinkButtons(contentTree.Defense.links);

    return (
        <DebouncedLoader loading={loading} delay={1000} size={40} centered={true}>
            <IntlProvider locale={locale} key={locale} defaultLocale="en" messages={messages}>
                <Box
                    width="100%"
                    component="main"
                    sx={{
                        padding: 0,
                        flexGrow: 0,
                        p: 0,
                    }}
                >
                    <PrefilteredResourceLibraryItemsProvider
                        initialFilters={{
                            productApplicationKeys: ['Aerospace, Defense & Medical'],
                        }}
                    >
                        <ReusableTabs
                            bannerImage={contentTree.pageTitleBackgroundImage.imageUrl}
                            bannerTitle={contentTree.pageTitle.content}
                            bannerDescription={contentTree.pageSubtitle.content}
                            tabLabels={[
                                contentTree.tabs.Overview.title.content,
                                contentTree.tabs.Resources.title.content,
                                contentTree.tabs.SimulationTools.title.content,
                            ]}
                            tabKeys={['Overview', 'Resources', 'SimulationTools']}
                            tabContents={[
                                <AerospaceTemplate
                                    key="overview"
                                    aerospaces={telecommunicationTemplates}
                                    aerospaceLinkButtons={aerospaceLinkButtons}
                                    avionicsLinkButtons={avionicsLinkButtons}
                                    defenseLinkButtons={defenseLinkButtons}
                                    temperatureSensorLinks={contentTree.temperatureSensorLinks}
                                />,
                                <TechnicalResources key="resources" backgroundColor={'white'} />,
                                <ToolPage
                                    key="simulation-tools"
                                    cmsContent={toolsContent.Overview}
                                />,
                            ]}
                            bannerFontStyle={styles.bannerFont}
                            subBannerFontStyle={styles.bannerFont2}
                            pageName={'aerospace'}
                            showTabs={true}
                            breadcrumbs={
                                <BreadCrumbs navigationPath="/Home/Applications/AerospaceDefenseMedical" />
                            }
                        />
                    </PrefilteredResourceLibraryItemsProvider>
                </Box>
            </IntlProvider>
        </DebouncedLoader>
    );
};

export default AerospacePage;
